import type { Quote } from '@client/model';

export type BenefitPeriod = number | 'lifetime';

export const LIFETIME_PERIOD = 'lifetime' as const;

export function parseDurationYears(value?: string): BenefitPeriod {
  if (!value) return 0;

  const lower = value.toLowerCase();
  if (lower.includes('lifetime') || lower.includes('unlimited')) {
    return LIFETIME_PERIOD;
  }

  const match = lower.match(/(\d+(\.\d+)?)\s*(years?|months?|yrs?)/);
  if (!match) return 0;

  const num = parseFloat(match[1]);
  const unit = match[3];
  return unit.startsWith('m') ? num / 12 : num;
}

export function getBenefitPeriodFromQuote(quote: Quote): BenefitPeriod {
  const aobYears = quote.aobDuration ? parseDurationYears(quote.aobDuration) : null;
  const cobYears = quote.cobDuration ? parseDurationYears(quote.cobDuration) : null;

  if (aobYears !== null || cobYears !== null) {
    if (aobYears === LIFETIME_PERIOD || cobYears === LIFETIME_PERIOD) {
      return LIFETIME_PERIOD;
    }
    // Both legs present: sum them (total benefit period)
    if (aobYears !== null && cobYears !== null) {
      return (aobYears as number) + (cobYears as number);
    }
    // Only one leg present: use it directly
    return (aobYears ?? cobYears) as BenefitPeriod;
  }

  if (quote.ltcBenefitDurationMonths) {
    return quote.ltcBenefitDurationMonths / 12;
  }

  return 6;
}

export function benefitPeriodsMatch(period1: BenefitPeriod, period2: BenefitPeriod): boolean {
  if (period1 === LIFETIME_PERIOD && period2 === LIFETIME_PERIOD) {
    return true;
  }

  if (period1 === LIFETIME_PERIOD || period2 === LIFETIME_PERIOD) {
    return false;
  }

  return Math.abs(period1 - period2) < 0.1;
}

export function formatBenefitPeriod(period: BenefitPeriod): string {
  if (period === LIFETIME_PERIOD) {
    return 'Lifetime';
  }

  return `${period} ${period === 1 ? 'year' : 'years'}`;
}

export function benefitPeriodToString(period: BenefitPeriod): string {
  return period === LIFETIME_PERIOD ? LIFETIME_PERIOD : period.toString();
}

export function stringToBenefitPeriod(value: string): BenefitPeriod {
  if (value === LIFETIME_PERIOD) {
    return LIFETIME_PERIOD;
  }

  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
}
